/**
 * Cálculos de la pestaña «Hoy» del club (maqueta Club-Hoy): días del
 * selector, bloques por cancha con su duración real (60/90 min) y ocupación.
 * Funciones puras para poder probarlas sin pintar la pantalla.
 */
import type { ClubTodayCell, ClubTodayCourt } from "../../lib/api";

/** Píxeles por minuto en la cuadrícula de escritorio (una hora = 72 px). */
export const PX_PER_MIN = 1.2;
/** Escala compacta de «Todas las canchas» en el celular. */
export const PX_PER_MIN_COMPACT = 0.5;

export type BlockKind = "free" | "reserved" | "pending" | "tournament" | "blocked";

export interface DayBlock {
  kind: BlockKind;
  /** Inicio/fin del bloque en la cuadrícula (turnos que ocupa). */
  start: string;
  end: string;
  startMin: number;
  endMin: number;
  /** Horario que se muestra (el real de la reserva cuando lo hay). */
  timeLabel: string;
  label: string | null;
  detail: string | null;
  reservationId?: number;
}

export function toMinutes(value: string): number {
  const [h, m] = value.split(":").map((p) => Number(p));
  return (h || 0) * 60 + (m || 0);
}

export function fromMinutes(total: number): string {
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export function isoFromDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function dateFromIso(iso: string): Date {
  return new Date(`${iso}T12:00:00`);
}

const WEEKDAY_SHORT = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export interface DayOption {
  iso: string;
  top: string;
  bottom: string;
}

/** Hoy, Mañana y los 5 días siguientes. */
export function dayOptions(todayIso: string): DayOption[] {
  const base = dateFromIso(todayIso);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const wd = WEEKDAY_SHORT[d.getDay()];
    if (i === 0) return { iso: isoFromDate(d), top: "Hoy", bottom: `${wd} ${d.getDate()}` };
    if (i === 1) return { iso: isoFromDate(d), top: "Mañana", bottom: `${wd} ${d.getDate()}` };
    return { iso: isoFromDate(d), top: wd, bottom: String(d.getDate()) };
  });
}

function cellKey(cell: ClubTodayCell): string | null {
  // Cada turno libre y cada partido de torneo es su propio bloque.
  if (cell.kind === "free" || cell.kind === "tournament") return null;
  if (cell.reservation_id != null) return `r${cell.reservation_id}`;
  return `${cell.kind}|${cell.label ?? ""}|${cell.detail ?? ""}`;
}

/** Une los turnos consecutivos de una misma reserva/bloqueo/partido en un solo
 * bloque: así una reserva de 3 h en cancha de 90 min es un bloque, no dos. */
export function courtBlocks(court: Pick<ClubTodayCourt, "slots">): DayBlock[] {
  const out: DayBlock[] = [];
  const keys: Array<string | null> = [];
  for (const cell of court.slots) {
    const key = cellKey(cell);
    const prev = out[out.length - 1];
    if (prev && key !== null && keys[keys.length - 1] === key && prev.end === cell.start_time) {
      prev.end = cell.end_time;
      prev.endMin = toMinutes(cell.end_time);
      if (cell.reservation_id == null) prev.timeLabel = `${prev.start}–${prev.end}`;
      continue;
    }
    const kind = (cell.kind as BlockKind) ?? "free";
    const realStart = cell.res_start ?? cell.start_time;
    const realEnd = cell.res_end ?? cell.end_time;
    out.push({
      kind,
      start: cell.start_time,
      end: cell.end_time,
      startMin: toMinutes(cell.start_time),
      endMin: toMinutes(cell.end_time),
      timeLabel: `${realStart}–${realEnd}`,
      label: cell.label,
      detail: cell.detail,
      reservationId: cell.reservation_id,
    });
    keys.push(key);
  }
  return out;
}

/** Versión corta de un nombre para turnos de 60 min, SIN puntos suspensivos:
 * «Jorge Alberto Méndez Ruiz / Luis Ángel Cervantes» → «Jorge A. Méndez / Luis Á. Cervantes».
 * Solo se acorta si hay varias personas o el texto es largo (> 32 letras);
 * nombres de 1–2 palabras quedan igual y se saltan «de», «la», etc. */
const PARTICLES = new Set(["de", "del", "la", "las", "los", "y"]);
export function shortPeopleName(text: string): string {
  if (!text.includes(" / ") && text.length <= 32) return text;
  return text
    .split(" / ")
    .map((part) => {
      const words = part.trim().split(/\s+/).filter((w) => w && !PARTICLES.has(w.toLowerCase()));
      if (words.length < 3) return words.join(" ");
      return `${words[0]} ${words[1].charAt(0)}. ${words[2]}`;
    })
    .join(" / ");
}

export interface Occupancy {
  taken: number;
  total: number;
  percent: number;
}

/** Ocupación del día por turnos: reservadas / turnos reservables (no cuenta
 * bloqueadas ni los de torneo). */
export function courtOccupancy(court: Pick<ClubTodayCourt, "slots">): Occupancy {
  let taken = 0;
  let total = 0;
  for (const cell of court.slots) {
    if (cell.kind === "blocked" || cell.kind === "tournament") continue;
    total += 1;
    if (cell.kind === "reserved" || cell.kind === "pending") taken += 1;
  }
  return { taken, total, percent: total > 0 ? Math.round((taken / total) * 100) : 0 };
}

/** Ventana común (de la que abre más temprano a la que cierra más tarde),
 * redondeada a horas completas. */
export function dayWindow(courts: Array<Pick<ClubTodayCourt, "slots">>): { startMin: number; endMin: number } | null {
  let start = Infinity;
  let end = -Infinity;
  for (const court of courts) {
    for (const cell of court.slots) {
      start = Math.min(start, toMinutes(cell.start_time));
      end = Math.max(end, toMinutes(cell.end_time));
    }
  }
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { startMin: Math.floor(start / 60) * 60, endMin: Math.ceil(end / 60) * 60 };
}

/** ¿El bloque ya pasó? Días anteriores: todo; hoy: lo que terminó antes de ahora. */
export function isPastBlock(block: Pick<DayBlock, "endMin">, dateIso: string, todayIso: string, nowMin: number): boolean {
  if (dateIso < todayIso) return true;
  if (dateIso > todayIso) return false;
  return block.endMin <= nowMin;
}
